// src/components/Sidebar.jsx
import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
  Divider,
  useMediaQuery,
  Avatar,
  Stack,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { NavLink, useLocation } from "react-router-dom";

// Icons
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LivestockIcon from "./icons/LivestockIcon";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ScienceIcon from "@mui/icons-material/Science";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleIcon from "@mui/icons-material/People";
import useAuth from "../hooks/useAuth";
import {
  getDisplayName,
  getRolesSummary,
  getInitials,
} from "../utils/userDisplay";
import { isPhase1SidebarNavEnabled } from "../config/appScope";
import { APP_HOME_PATH } from "../config/routes";

const DRAWER_WIDTH = 260;
const MINI_WIDTH = 72;

const DrawerPaper = styled("div")(({ theme }) => ({
  width: DRAWER_WIDTH,
  borderRight: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

/** @param {string} path */
function isTreatmentsSectionActive(path) {
  return (
    path === "/treatments" ||
    path.startsWith("/treatments/") ||
    path === "/treatment" ||
    path.startsWith("/treatment/")
  );
}

function isMedicinesSectionActive(path) {
  return path === "/medicines" || path.startsWith("/medicines/");
}

const items = [
  { label: "Dashboard", to: APP_HOME_PATH, icon: <DashboardIcon /> },
  { label: "Animals", to: "/animals", icon: <LivestockIcon /> },
  {
    label: "Treatments",
    to: "/treatments",
    icon: <LocalHospitalIcon />,
    isActive: isTreatmentsSectionActive,
  },
  {
    label: "Medicines",
    to: "/medicines",
    icon: <ScienceIcon />,
    isActive: isMedicinesSectionActive,
  },
  {
    label: "Lab / Health",
    to: "/treatment/1",
    icon: <ScienceIcon />,
    hidden: true,
  },
  { label: "Milking", to: "/milking", icon: <LocalDrinkIcon /> },
  { label: "Income", to: "/income", icon: <AttachMoneyIcon /> },
  { label: "Expenses", to: "/expenses", icon: <MoneyOffIcon /> },
  { label: "Stock Taking", to: "/stock-taking", icon: <ReceiptLongIcon /> },
  { label: "Calves", to: "/calves", icon: <ChildCareIcon /> },
  { label: "Activities", to: "/planning/activities", icon: <EventNoteIcon /> },
  { label: "Calendar", to: "/planning/calendar", icon: <CalendarMonthIcon /> },
  // Admin group (dashboard is /; /admin index redirects home)
  { divider: true },
  { label: "Users", to: "/admin/users", icon: <PeopleIcon /> },
];

function NavItem({ to, icon, label, mini, active, onAfterNavigate }) {
  const content = (
    <ListItemButton
      component={NavLink}
      to={to}
      onClick={() => onAfterNavigate?.()}
      selected={active}
      sx={{
        borderRadius: 2,
        mx: 1,
        mb: 0.5,
        py: 1.1,
        "&.Mui-selected": {
          background:
            "linear-gradient(135deg, rgba(30,144,255,0.12), rgba(0,191,255,0.12))",
          "& .MuiListItemIcon-root": { color: "primary.main" },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, justifyContent: "center" }}>
        {icon}
      </ListItemIcon>
      {!mini && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontWeight: 600 }}
        />
      )}
    </ListItemButton>
  );

  return mini ? (
    <Tooltip title={label} placement="right">
      {content}
    </Tooltip>
  ) : (
    content
  );
}

function SidebarUserFooter({ mini, onNavigate, profileActive }) {
  const { user } = useAuth();
  if (!user) return null;

  const name = getDisplayName(user);
  const roles = getRolesSummary(user);
  const initials = getInitials(user);
  const tip = `${name} · ${roles}`;

  if (mini) {
    return (
      <Box
        sx={{
          py: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Tooltip title={tip} placement="right">
          <IconButton
            component={NavLink}
            to="/profile"
            onClick={() => onNavigate?.()}
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              ...(profileActive && {
                boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
              }),
            }}
            aria-label="My profile"
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: "0.85rem",
                fontWeight: 700,
                background:
                  "linear-gradient(135deg, rgba(42,152,255,0.85), rgba(2,130,172,0.85))",
              }}
            >
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      component={NavLink}
      to="/profile"
      onClick={() => onNavigate?.()}
      sx={{
        textDecoration: "none",
        color: "inherit",
        px: 1.5,
        py: 1.25,
        mx: 1,
        mb: 1,
        borderRadius: 2,
        display: "block",
        ...(profileActive && {
          background:
            "linear-gradient(135deg, rgba(30,144,255,0.12), rgba(0,191,255,0.12))",
        }),
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: "0.9rem",
            fontWeight: 700,
            flexShrink: 0,
            background:
              "linear-gradient(135deg, rgba(42,152,255,0.85), rgba(2,130,172,0.85))",
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {roles}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function Sidebar({
  openMini,
  onToggleMini,
  mobileOpen,
  onCloseMobile,
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();

  /** Mini rail only on desktop; mobile drawer always shows labels (avoids blank strip + hidden labels). */
  const effectiveMini = isMdUp && openMini;
  const width = effectiveMini ? MINI_WIDTH : DRAWER_WIDTH;

  const content = (
    <DrawerPaper
      style={{
        width,
        transition: "width 220ms ease",
        overflowX: "hidden",
      }}
    >
      {/* Spaces the content under the AppBar */}
      <Toolbar
        sx={{
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: effectiveMini ? "center" : "space-between",
          minHeight: 64,
        }}
      >
        {!effectiveMini && <Box sx={{ fontWeight: 700 }}>Menu</Box>}
        {isMdUp ? (
          <IconButton
            size="small"
            onClick={onToggleMini}
            aria-label={openMini ? "Expand sidebar" : "Collapse sidebar"}
            sx={{ border: (t) => `1px solid ${t.palette.divider}` }}
          >
            {openMini ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={onCloseMobile}
            aria-label="Close menu"
            sx={{ border: (t) => `1px solid ${t.palette.divider}` }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>

      <Divider />

      <Box sx={{ p: 1, flex: 1, overflowY: "auto" }}>
        <List sx={{ py: 0 }}>
          {items
            .filter((it) => {
              if (it.divider || it.hidden) return true;
              return isPhase1SidebarNavEnabled(it.to);
            })
            .filter((it, idx, arr) => {
              if (!it.divider) return true;
              const prev = arr[idx - 1];
              const next = arr[idx + 1];
              if (prev?.divider || !next || next.divider) return false;
              return true;
            })
            .map((it, idx) =>
              it.divider ? (
                <Divider key={`div-${idx}`} sx={{ my: 1 }} />
              ) : it.hidden ? null : (
                <NavItem
                  key={it.to}
                  to={it.to}
                  icon={it.icon}
                  label={it.label}
                  mini={effectiveMini}
                  onAfterNavigate={isMdUp ? undefined : onCloseMobile}
                  active={
                    typeof it.isActive === "function"
                      ? it.isActive(location.pathname)
                      : it.to === APP_HOME_PATH
                        ? location.pathname === APP_HOME_PATH
                        : location.pathname === it.to ||
                          location.pathname.startsWith(`${it.to}/`)
                  }
                />
              )
            )}
        </List>
      </Box>

      <Divider sx={{ mx: 1 }} />

      <SidebarUserFooter
        mini={effectiveMini}
        onNavigate={onCloseMobile}
        profileActive={location.pathname === "/profile"}
      />
    </DrawerPaper>
  );

  // Permanent drawer on md+
  if (isMdUp) {
    return (
      <Drawer
        variant="permanent"
        open
        PaperProps={{
          sx: {
            width,
            boxSizing: "border-box",
            borderRight: (t) => `1px solid ${t.palette.divider}`,
            overflow: "hidden",
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  // Temporary drawer on small screens
  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onCloseMobile}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
        },
      }}
    >
      {content}
    </Drawer>
  );
}
